import React, { HTMLProps } from "react";
import { Dropdown } from "react-bootstrap";
import {
  SortIcon,
  DropdownDots,
  OnlineSuccess,
  OfflineIcon,
  ToggleIconLeft,
} from "../../components/ui/icons";
import DeleteModal from "../../components/deleteModal";
import CopyGroupHirerarchyModal from "../../components/copyGroupHirerarchyModal";
import DeleteDeviceModal from "../../components/deleteDeviceModal";
import UnassignDeviceModal from "../../components/updateDeviceModal/unassignDeviceModal";
import { usePermissions } from "../../customHooks/usePermissions";
import { setEditGroupData } from "../../components/createEditGroupModal/editGroupSlice";
import {
  setEditDeviceData,
  showModal,
} from "../../components/updateDeviceModal/updateDeviceModalSlice";
import { actions as modalActions } from "../../components/createEditGroupModal/createEditGroupModalSlice";
import { useDispatch } from "react-redux";
import { DefaultTopLevelGroup } from "../../common/constants";
import Modal4RebootDevice from "../../components/modals/modal4RebootDevice";

const IndeterminateCheckbox = ({
  indeterminate,
  className = "",
  ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) => {
  const ref = React.useRef<HTMLInputElement>(null!);

  React.useEffect(() => {
    if (typeof indeterminate === "boolean") {
      ref.current.indeterminate = indeterminate;
    }
  }, [ref, indeterminate]);

  return (
    <input
      type="checkbox"
      ref={ref}
      className={className + " cursor-pointer"}
      {...rest}
    />
  );
};

const DeviceManagementColumns = ({ onRowSelectionChange, RowType }) => {
  const { hasPermissions } = usePermissions();
  const dispatch = useDispatch();

  const onEditDevice = (
    orgId: string,
    displayName: string,
    deviceId: string
  ): void => {
    dispatch(showModal());
    dispatch(setEditDeviceData({ orgId, displayName, deviceId }));
  };

  const isRowChecked = (row) => {
    if (row.subRows && row.subRows.length > 0) {
      return row.getIsAllSubRowsSelected();
    }
    return row.getIsSelected();
  };

  return React.useMemo(
    () => [
      {
        accessorFn: (row) =>
          row.parentHierarchyDetails
            ? row.parentHierarchyDetails.parentHierarchy?.length
              ? `${[row.parentHierarchyDetails.parentHierarchy]}`
              : `Unassigned Devices`
            : `${row.deviceDisplayName}`,
        id: "deviceName",
        header: ({ table }) => (
          <>
            Device Name{" "}
            <span>
              <SortIcon />
            </span>
          </>
        ),
        cell: ({ row, getValue }) => (
          <div
            style={{
              paddingLeft: `${row.depth * 1.5}rem`,
            }}
          >
            <>
              <IndeterminateCheckbox
                {...{
                  checked: isRowChecked(row),
                  indeterminate: row.getIsSomeSelected(),
                  onChange: onRowSelectionChange(row),
                }}
              />{" "}
              {getValue()}
            </>
          </div>
        ),
        enableColumnFilter: false,
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => `${row?.deviceId}`,
        accessorKey: "deviceId",
        header: (row) => (
          <div className="table-header">
            Device Id <SortIcon />
          </div>
        ),
        footer: (props) => props.column.id,
        enableColumnFilter: false,
        cell: (row) => (
          <div className="table-cell">
            {row.getValue()
              ? row.getValue() === "undefined"
                ? ""
                : row.getValue()
              : ""}
          </div>
        ),
      },
      {
        accessorKey: "deviceStatus",
        header: (row) => (
          <div className="table-header">
            Status <SortIcon />
          </div>
        ),
        footer: (props) => props.column.id,
        enableColumnFilter: false,
        cell: (row) => (
          <div className="table-cell online">
            <div className="online-success">
              {row.getValue() ? (
                row.getValue() === "connected" ? (
                  <OnlineSuccess />
                ) : (
                  <OfflineIcon />
                )
              ) : (
                <></>
              )}
            </div>
            <div className="success-text">
              {row.getValue()
                ? row.getValue() === "connected"
                  ? "Online"
                  : "Offline"
                : ""}
            </div>
          </div>
        ),
      },
      {
        accessorFn: (row) =>
          row?.iotAgentVersion !== undefined &&
          row?.iotAgentVersion !== "undefined"
            ? row?.iotAgentVersion
            : row?.applicationVersion,
        accessorKey: "iotAgentVersion",
        header: (row) => (
          <div className="table-header">
            IoT Agent/Application Version <SortIcon />
          </div>
        ),
        footer: (props) => props.column.id,
        enableColumnFilter: false,
        cell: (row) => (
          <div className="table-cell">
            {row.getValue()
              ? row.getValue() === "undefined"
                ? ""
                : row.getValue()
              : ""}
          </div>
        ),
      },
      {
        accessorFn: (row) =>
          row?.firmwareVersion !== undefined &&
          row?.firmwareVersion !== "undefined"
            ? row?.firmwareVersion
            : row?.androidVersion,
        accessorKey: "firmwareVersion",
        header: (row) => (
          <div className="table-header">
            Firmware/Android Version <SortIcon />
          </div>
        ),
        footer: (props) => props.column.id,
        enableColumnFilter: false,
        cell: (row) => (
          <div className="table-cell">
            {row.getValue()
              ? row.getValue() === "undefined"
                ? ""
                : row.getValue()
              : ""}
          </div>
        ),
      },
      {
        accessorFn: (row) =>
          row.parentHierarchyDetails ? row.parentHierarchyDetails : row,
        accessorKey: "deviceType",
        header: (row) => (
          <div className="table-header">
            Device Type <SortIcon />
          </div>
        ),
        footer: (props) => props.column.id,
        enableColumnFilter: false,
        cell: ({ row, getValue }) => {
          return (
            <div className="table-cell">
              {getValue().rowType === RowType.DEVICE &&
                (row?.deviceType !== undefined &&
                row?.deviceType !== "undefined"
                  ? row?.deviceType
                  : "Hardware")}
            </div>
          );
        },
      },
      {
        accessorFn: (row) =>
          row.parentHierarchyDetails ? row.parentHierarchyDetails : row,
        id: "actions",
        header: () => <span></span>,
        Cell: () => <ToggleIconLeft />,
        footer: (props) => props.column.id,
        enableColumnFilter: false,
        cell: ({ row, getValue }) => {
          const groupArray: string =
            row?.original?.parentHierarchyDetails?.parentHierarchy;
          const groupId: string =
            row?.original?.parentHierarchyDetails?.groupId;
          const deviceDisplayName: string = row?.original?.deviceDisplayName;
          const deviceId: string = row?.original?.deviceId;
          const parentHierarchy: string =
            row?.original?.parentHierarchyDetails?.parentHierarchy;
          const organizations = row?.original.organizations;
          let groupDisplayName: string;
          if (groupArray) {
            groupDisplayName = groupArray;
          }
          const deviceGroupId: string =
            row.getParentRow()?.original.parentHierarchyDetails.groupId;
          const groupHierarchy: string = row?.original?.groupHierarchy;
          return (
            <div className="actions-btns">
              {hasPermissions("user.edit") && (
                <Dropdown>
                  <Dropdown.Toggle id="dropdowndots">
                    <DropdownDots />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {getValue().rowType === RowType.DEVICE ? (
                      <>
                        <Dropdown.Item>
                          <div
                            onClick={() =>
                              onEditDevice(
                                row?.getParentRow().original
                                  .parentHierarchyDetails.groupId,
                                deviceDisplayName,
                                deviceId
                              )
                            }
                          >
                            Edit
                          </div>
                        </Dropdown.Item>
                        {!(
                          organizations?.length === 1 &&
                          organizations[0] == DefaultTopLevelGroup.ID
                        ) && (
                          <Dropdown.Item>
                            <UnassignDeviceModal
                              deviceDisplayName={getValue().deviceDisplayName}
                              deviceId={getValue().deviceId}
                              deviceGroupId={deviceGroupId}
                            />
                          </Dropdown.Item>
                        )}
                        <Dropdown.Item>
                          <DeleteDeviceModal
                            deviceDisplayName={getValue().deviceDisplayName}
                            deviceId={getValue().deviceId}
                          />
                        </Dropdown.Item>
                        <Dropdown.Item disabled={!row?.original?.deviceId}>
                          {row?.original?.deviceId && (
                            <Modal4RebootDevice
                              deviceId={row.original.deviceId}
                              deviceDisplayName={row.original.deviceDisplayName}
                            />
                          )}
                        </Dropdown.Item>
                      </>
                    ) : (
                      <>
                        {groupId !== DefaultTopLevelGroup.ID && (
                          <Dropdown.Item>
                            <DeleteModal groupOrgId={groupId} groupDisplayName={groupDisplayName} />
                          </Dropdown.Item>
                      )}
                        <Dropdown.Item>
                          <CopyGroupHirerarchyModal
                            groupHierarchy={groupHierarchy}
                          />
                        </Dropdown.Item>
                      </>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </div>
          );
        },
      },
    ],
    [onRowSelectionChange, RowType]
  );
};

export default DeviceManagementColumns;
